// Creator Blueprint — public step-by-step guide for new creators on how
// to go live + work the platform. Pulls heavily from the two reference
// infographics the founder uploaded (TAP IN TV + TAP IN RADIO 6-step
// guides) and adds clickable action links so a new creator can read a
// step and immediately go do it.
import React from "react";
import { Link } from "react-router-dom";
import {
  Radio, Video, Mic, Users, Sparkles, Headphones, Wifi, Camera,
  Mic2, Sliders, MonitorPlay, Tv, Send, Heart, ChevronRight,
  CheckCircle, Star, MessageCircle, ArrowRight, AlertCircle,
} from "lucide-react";
import LiveMatchWidget from "../components/LiveMatchWidget";

// User's uploaded reference infographics (these became the source of
// truth for the Blueprint design).
const HERO_INFOGRAPHIC = "https://customer-assets.emergentagent.com/job_live-broadcast-53/artifacts/rpk6lqgp_ad80e3f8-39fb-4340-83ba-4761ed330d32%20%281%29.png";
const RADIO_INFOGRAPHIC = "https://customer-assets.emergentagent.com/job_live-broadcast-53/artifacts/zc3d2yfu_e3ee24f6-e97c-40de-a6b9-b13deb605b0b.png";

const TV_STEPS = [
  { n: 1, title: "Set up your video", icon: Video, body: ["Connect your camera(s)", "Arrange your scenes", "Add overlays, lower thirds, tickers, and more"], cta: "Open Director's Mode", to: "/studio/live", testid: "blueprint-tv-step-1" },
  { n: 2, title: "Invite guests", icon: Users, body: ['Click "Copy full guest link"', "Send the link to guests", "Manage guests on screen"], cta: "Manage guests", to: "/studio/live", testid: "blueprint-tv-step-2" },
  { n: 3, title: "Test your setup", icon: CheckCircle, body: ["Check camera angles", "Test audio levels", "Make sure everything looks and sounds great"], cta: "Run pre-flight check", to: "/studio/live", testid: "blueprint-tv-step-3" },
  { n: 4, title: "Add graphics & branding", icon: Sparkles, body: ["Add your logo, overlays and tickers", "Customize lower thirds", "Set up your scenes"], cta: "Open Branding tab", to: "/studio/live", testid: "blueprint-tv-step-4" },
  { n: 5, title: "Go live", icon: Radio, body: ["Click the START button", "You are now live to your audience!", "Stream to multiple platforms at once"], cta: "Go live now", to: "/studio/live", testid: "blueprint-tv-step-5" },
  { n: 6, title: "Engage & grow", icon: Heart, body: ["Interact with your audience in real time", "Monitor your stream and performance", "Keep the energy going!"], cta: "View Dashboard", to: "/dashboard", testid: "blueprint-tv-step-6" },
];

const RADIO_STEPS = [
  { n: 1, title: "Select your music", icon: Mic2, body: ["Browse built-in library", "Or connect Spotify", "Create or load your playlist"], cta: "Open DJ Studio", to: "/radio/host", testid: "blueprint-radio-step-1" },
  { n: 2, title: "Mix & prepare", icon: Sliders, body: ["Use decks, crossfader, and effects", "Connect your MIDI controller for a real DJ experience"], cta: "Configure decks", to: "/radio/host", testid: "blueprint-radio-step-2" },
  { n: 3, title: "Test your audio", icon: Headphones, body: ["Check your levels", "Test microphone", "Monitor your mix"], cta: "Audio check", to: "/radio/host", testid: "blueprint-radio-step-3" },
  { n: 4, title: "Schedule (optional)", icon: Send, body: ["Schedule content", "Set ads and announcements", "Auto-rotate playlists"], cta: "Schedule playlist", to: "/radio/host", testid: "blueprint-radio-step-4" },
  { n: 5, title: "Go live", icon: Radio, body: ["Click the GO LIVE button", "You are now broadcasting live to your radio station", "Listeners can tune in from anywhere"], cta: "Start broadcast", to: "/radio/host", testid: "blueprint-radio-step-5" },
  { n: 6, title: "Engage & grow", icon: Heart, body: ["Interact with your listeners", "Check requests & calls", "Grow your audience"], cta: "View Radio dashboard", to: "/radio/host", testid: "blueprint-radio-step-6" },
];

const RECOMMENDED_SETUP = [
  { icon: Camera, title: "Camera(s)", sub: "DSLR / PTZ / Webcam" },
  { icon: Mic, title: "Microphones", sub: "XLR / USB" },
  { icon: Sliders, title: "Audio Interface", sub: "For high-quality sound" },
  { icon: MonitorPlay, title: "MIDI Controller", sub: "For DJ control" },
  { icon: Headphones, title: "Headphones", sub: "Monitor your audio" },
  { icon: Wifi, title: "Stable Internet", sub: "Wired or 10Mbps+" },
];

const PRO_TIPS = [
  "Always do a test run before going live.",
  "Use a wired internet connection for best stability.",
  "Engage with your audience and have fun!",
  "Keep your camera at eye level and use natural lighting.",
  "Pre-write your scenes & lower thirds — fewer fumbles on air.",
];

function StepCard({ step, accent }) {
  const Icon = step.icon;
  return (
    <div data-testid={step.testid} className="relative rounded-xl bg-white/5 ring-1 ring-white/10 p-4 hover:ring-tv-accent/40 transition group">
      <div className={`absolute -top-3 -left-3 w-9 h-9 rounded-full flex items-center justify-center font-display font-black text-sm shadow-lg ${accent}`}>
        {step.n}
      </div>
      <div className="flex items-start gap-3 mt-1">
        <Icon className="w-5 h-5 text-tv-accent shrink-0 mt-0.5" />
        <h3 className="font-display font-black text-base text-white tracking-tight uppercase">{step.title}</h3>
      </div>
      <ul className="mt-3 space-y-1.5">
        {step.body.map((b, i) => (
          <li key={i} className="text-sm text-white/75 flex items-start gap-2">
            <span className="text-tv-accent mt-0.5">•</span> {b}
          </li>
        ))}
      </ul>
      {step.to && (
        <Link
          to={step.to}
          className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-tv-accent hover:text-yellow-300 group-hover:translate-x-1 transition"
        >
          {step.cta} <ChevronRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}

export default function CreatorBlueprint() {
  return (
    <div className="min-h-screen bg-[#0a0b10] text-white pb-24" data-testid="creator-blueprint">
      {/* Hero */}
      <header className="relative overflow-hidden border-b-4 border-red-600">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/40 via-black to-black" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
          <div className="text-[11px] uppercase tracking-[0.4em] text-tv-accent font-bold">Creator Blueprint</div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-7xl mt-2 leading-none">
            <span className="text-red-500">GO LIVE</span> IN <span className="text-tv-accent">6 EASY STEPS</span>
          </h1>
          <p className="text-white/70 text-base sm:text-lg mt-4 max-w-2xl">
            Broadcast video + radio to the world — from anywhere. One platform. Two powerful systems. Unlimited possibilities.
          </p>
          <div className="flex flex-wrap gap-3 mt-6" data-testid="blueprint-hero-ctas">
            <Link to="/studio/live" data-testid="blueprint-cta-tv" className="bg-tv-live hover:bg-red-600 text-white font-bold px-5 py-3 rounded-full inline-flex items-center gap-2 shadow-lg shadow-red-500/30">
              <Video className="w-4 h-4" /> Start a Video Stream
            </Link>
            <Link to="/radio/host" data-testid="blueprint-cta-radio" className="bg-tv-accent hover:bg-yellow-300 text-black font-bold px-5 py-3 rounded-full inline-flex items-center gap-2">
              <Radio className="w-4 h-4" /> Start a Radio Show
            </Link>
            <Link to="/apply-creator" data-testid="blueprint-cta-apply" className="bg-white/10 hover:bg-white/20 text-white font-bold px-5 py-3 rounded-full inline-flex items-center gap-2 ring-1 ring-white/15">
              <Star className="w-4 h-4" /> Apply to be a Creator
            </Link>
          </div>
        </div>
      </header>

      {/* Reference infographic — full poster */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="blueprint-infographic">
        <div className="rounded-2xl overflow-hidden ring-1 ring-white/10 bg-black">
          <img src={HERO_INFOGRAPHIC} alt="Tap In TV + Radio — Go Live in 6 Easy Steps" className="w-full h-auto" />
        </div>
        <div className="text-xs text-white/40 mt-2 text-center italic">The official Tap In TV + Radio broadcaster blueprint</div>
      </section>

      {/* Live Match — what's on right now + going live next */}
      <LiveMatchWidget />

      {/* TV Studio Steps */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="blueprint-tv-section">
        <div className="flex items-center gap-3 mb-6 border-b border-white/10 pb-3">
          <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center"><Tv className="w-5 h-5 text-white" /></div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-400 font-bold">Tap In TV</div>
            <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">Video Studio — 6 Steps</h2>
          </div>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {TV_STEPS.map((s) => <StepCard key={s.n} step={s} accent="bg-red-600 text-white" />)}
        </div>
      </section>

      {/* Radio Studio Steps */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="blueprint-radio-section">
        <div className="flex items-center gap-3 mb-6 border-b border-white/10 pb-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center"><Radio className="w-5 h-5 text-white" /></div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-blue-300 font-bold">Tap In Radio</div>
            <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">DJ Studio — 6 Steps</h2>
          </div>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {RADIO_STEPS.map((s) => <StepCard key={s.n} step={s} accent="bg-blue-500 text-white" />)}
        </div>

        {/* Radio infographic */}
        <div className="mt-10 rounded-2xl overflow-hidden ring-1 ring-white/10 bg-black" data-testid="blueprint-radio-infographic">
          <img src={RADIO_INFOGRAPHIC} alt="Tap In Radio — Custom Online Radio Studio" className="w-full h-auto" />
        </div>
      </section>

      {/* Recommended setup */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="blueprint-setup">
        <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold">Recommended Setup</div>
        <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight mb-6">Gear that makes you sound & look pro</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {RECOMMENDED_SETUP.map((r) => {
            const Icon = r.icon;
            return (
              <div key={r.title} className="rounded-xl bg-white/5 ring-1 ring-white/10 p-3 text-center">
                <Icon className="w-7 h-7 mx-auto text-tv-accent mb-2" />
                <div className="font-bold text-sm">{r.title}</div>
                <div className="text-[11px] text-white/50 mt-0.5">{r.sub}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pro Tips */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="blueprint-pro-tips">
        <div className="rounded-2xl bg-gradient-to-br from-yellow-500/15 via-black to-black ring-1 ring-tv-accent/30 p-5 sm:p-7">
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-5 h-5 text-tv-accent fill-tv-accent" />
            <h3 className="font-display font-black text-xl tracking-tight">Pro Tips</h3>
          </div>
          <ul className="grid sm:grid-cols-2 gap-2.5">
            {PRO_TIPS.map((t, i) => (
              <li key={i} className="text-sm text-white/85 flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-tv-accent shrink-0 mt-0.5" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* You're ready */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 text-center" data-testid="blueprint-ready">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-600/20 ring-1 ring-red-600/40 text-xs font-bold uppercase tracking-wider text-red-300 mb-4">
          <Radio className="w-3.5 h-3.5 animate-pulse" /> You're Ready!
        </div>
        <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter mb-3">Go live and share your voice with the world.</h2>
        <p className="text-white/70 mb-6">Pick your medium. Hit start. The network is waiting.</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/studio/live" className="bg-tv-live hover:bg-red-600 text-white font-bold px-6 py-3 rounded-full inline-flex items-center gap-2">
            <Video className="w-4 h-4" /> Open Video Studio <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/radio/host" className="bg-tv-accent hover:bg-yellow-300 text-black font-bold px-6 py-3 rounded-full inline-flex items-center gap-2">
            <Radio className="w-4 h-4" /> Open Radio Studio <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-8 text-xs text-white/50 flex items-center justify-center gap-2 flex-wrap" data-testid="blueprint-help">
          <MessageCircle className="w-3.5 h-3.5" /> Need help?
          <Link to="/support" className="text-tv-accent hover:underline font-bold">Visit our Help Center</Link>
          <span>·</span>
          <Link to="/contact" className="text-tv-accent hover:underline font-bold">Contact support</Link>
        </div>
      </section>
    </div>
  );
}
